import React, { useState, lazy, Suspense } from 'react';
import FooterAppDownload from '../components/FooterAppDownload';
import AppPromoSection from '../components/AppPromoSection';
import PostEventSection, { WHATSAPP_2027_URL } from '../components/PostEventSection';
import PreRegistro2027Section from '../components/PreRegistro2027Section';
import StickyPreregistroBar from '../components/StickyPreregistroBar';
import { PreregistroCounter } from '../components/PreregistroCounter';
import { AFTERMOVIE_ID } from '../components/ThankYouPopup';
import { trackPreregistroClick } from '../hooks/usePreregistroCount';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { useLanguage } from '../context/LanguageContext';
import { SpeakersSection } from '../components/sections/SpeakersSection';
import { PartnersSection } from '../components/sections/PartnersSection';
import { ARTICLES, getCategoryBySlug } from '../data/articles';
// ThankYouPopup lazy-loaded — never blocks initial paint / LCP
const ThankYouPopup = lazy(() => import('../components/ThankYouPopup'));
import {
  HUBSPOT_SPONSORSHIP_URL,
  PRE_REGISTRO_2027_URL,
} from '../config/constants';
import { 
  Calendar, MapPin, Award, Sparkles, Handshake, Trophy,
  Phone, Mail, ChevronRight, Star, Linkedin, Facebook,
  Instagram, Languages, X, ExternalLink, Menu, Play, Flame, Heart
} from 'lucide-react';

const LandingPage = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { language, setLanguage, t } = useLanguage();

  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });

  const toggleLanguage = () => {
    setLanguage(language === 'es' ? 'en' : 'es');
  };

  return (
    <div className="min-h-screen bg-white">
      {/* SEO Meta Tags are handled in index.html */}
      
      {/* Navbar */}
      <nav className="fixed top-0 w-full bg-white border-b border-gray-200 z-50 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <div className="flex items-center space-x-3">
              <img 
                src="https://customer-assets.emergentagent.com/job_7f31f70d-502e-4d8c-8121-da1f96e98875/artifacts/7kyh006f_Logo%20Convencio%CC%81n%20Ver2.png" 
                alt="20° Convención ASOFOM" 
                className="h-16 w-auto"
              />
            </div>
            {/* Desktop Menu */}
            <div className="hidden lg:flex items-center space-x-6">
              {/* Convención dropdown */}
              <div className="relative group" data-testid="landing-nav-convencion-dropdown">
                <button className="inline-flex items-center gap-1 text-[#1B3A5F] hover:text-[#FF6B35] transition-colors font-semibold">
                  {t('nav.convention')}
                  <ChevronRight className="w-3.5 h-3.5 rotate-90 transition-transform group-hover:rotate-[270deg]" />
                </button>
                <div className="absolute left-0 top-full pt-3 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50 min-w-[240px]">
                  <div className="bg-white rounded-xl shadow-2xl border border-gray-100 overflow-hidden">
                    <a href="#gracias" className="block px-4 py-3 hover:bg-gray-50 text-[#1B3A5F] hover:text-[#FF6B35] transition-colors font-semibold text-sm" data-testid="landing-nav-mas-convencion">
                      {language === 'es' ? 'Así se vivió · Galería' : 'How it was lived · Gallery'}
                    </a>
                    <a href="#speakers" className="block px-4 py-3 hover:bg-gray-50 text-[#1B3A5F] hover:text-[#FF6B35] transition-colors font-semibold text-sm border-t border-gray-100" data-testid="landing-nav-speakers">
                      {t('nav.speakers')}
                    </a>
                    <a href="/ranking" className="block px-4 py-3 bg-[#D4AF37]/10 text-[#1B3A5F] hover:text-[#FF6B35] transition-colors font-bold text-sm border-t border-gray-100" data-testid="landing-nav-ranking">
                      🏆 Ranking ASOFOM 2026
                    </a>
                  </div>
                </div>
              </div>
              <a href="/agenda" className="text-[#1B3A5F] hover:text-[#FF6B35] transition-colors font-semibold">
                {t('nav.agenda')}
              </a>
              <a href="/perspectivas" className="text-[#1B3A5F] hover:text-[#FF6B35] transition-colors font-semibold" data-testid="nav-perspectivas">
                Perspectivas
              </a>
              <a href="/app-asofom" className="text-[#1B3A5F] hover:text-[#FF6B35] transition-colors font-semibold" data-testid="landing-nav-app">
                📱 App
              </a>
              {/* Patrocinadores dropdown */}
              <div className="relative group" data-testid="landing-nav-sponsors-dropdown">
                <button className="inline-flex items-center gap-1 text-[#1B3A5F] hover:text-[#FF6B35] transition-colors font-semibold">
                  {language === 'es' ? 'Patrocinadores' : 'Sponsors'}
                  <ChevronRight className="w-3.5 h-3.5 rotate-90 transition-transform group-hover:rotate-[270deg]" />
                </button>
                <div className="absolute left-0 top-full pt-3 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50 min-w-[240px]">
                  <div className="bg-white rounded-xl shadow-2xl border border-gray-100 overflow-hidden">
                    <a href="#sponsors" className="block px-4 py-3 hover:bg-gray-50 text-[#1B3A5F] hover:text-[#FF6B35] transition-colors" data-testid="landing-nav-sponsors-view">
                      <div className="font-semibold text-sm">{language === 'es' ? 'Ver patrocinadores' : 'View sponsors'}</div>
                      <div className="text-xs text-gray-500 mt-0.5">{language === 'es' ? 'Las marcas que lo hicieron posible' : 'The brands that made it possible'}</div>
                    </a>
                    <Link to="/patrocinios" className="block px-4 py-3 hover:bg-[#FF6B35]/5 border-t border-gray-100 group/item" data-testid="landing-nav-patrocinios">
                      <div className="flex items-center gap-2">
                        <div className="font-bold text-sm text-[#FF6B35]">{language === 'es' ? 'Patrocinar 2027' : 'Sponsor 2027'}</div>
                        <Sparkles className="w-3.5 h-3.5 text-[#FF6B35]" />
                      </div>
                      <div className="text-xs text-gray-500 mt-0.5">{language === 'es' ? 'Únete a la lista de espera de la 21ª edición' : 'Join the 21st edition waitlist'}</div>
                    </Link>
                  </div>
                </div>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={toggleLanguage}
                className="text-gray-700 hover:text-[#FF6B35]"
              >
                <Languages className="w-5 h-5 mr-2" />
                {language === 'es' ? 'EN' : 'ES'}
              </Button>
              <Button 
                asChild
                data-testid="landing-nav-2027-btn"
                className="reservar-cta bg-[#FF6B35] hover:bg-[#E55A28] text-white font-semibold px-6 shadow-lg hover:shadow-xl transition-all"
              >
                <a href={PRE_REGISTRO_2027_URL} target="_blank" rel="noopener noreferrer" onClick={trackPreregistroClick}>
                  <Flame className="w-4 h-4 mr-1.5" />
                  {language === 'es' ? 'Pre-regístrate 2027' : 'Pre-register 2027'}
                </a>
              </Button>
            </div>
            {/* Mobile Menu Button */}
            <div className="lg:hidden flex items-center space-x-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={toggleLanguage}
              >
                {language === 'es' ? 'EN' : 'ES'}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="p-2"
              >
                {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </Button>
            </div>
          </div>
        </div>
        
        {/* Mobile Menu Dropdown */}
        {mobileMenuOpen && (
          <div className="lg:hidden bg-white border-t border-gray-200 shadow-lg">
            <div className="px-4 py-4 space-y-3">
              <div className="py-2 border-b border-gray-100">
                <div className="text-[#1B3A5F] font-bold py-1">{t('nav.convention')}</div>
                <a
                  href="#gracias"
                  className="block text-[#1B3A5F]/80 hover:text-[#FF6B35] transition-colors font-medium py-1.5 pl-4"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {language === 'es' ? 'Así se vivió · Galería' : 'How it was lived · Gallery'}
                </a>
                <a
                  href="#speakers"
                  className="block text-[#1B3A5F]/80 hover:text-[#FF6B35] transition-colors font-medium py-1.5 pl-4"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {t('nav.speakers')}
                </a>
                <a
                  href="/ranking"
                  className="block text-[#1B3A5F] font-bold py-1.5 pl-4"
                  onClick={() => setMobileMenuOpen(false)}
                  data-testid="landing-mobile-nav-ranking"
                >
                  🏆 Ranking ASOFOM 2026
                </a>
              </div>
              <a 
                href="/agenda" 
                className="block text-[#1B3A5F] hover:text-[#FF6B35] transition-colors font-semibold py-2 border-b border-gray-100"
                onClick={() => setMobileMenuOpen(false)}
              >
                {t('nav.agenda')}
              </a>
              <a 
                href="/perspectivas" 
                className="block text-[#1B3A5F] hover:text-[#FF6B35] transition-colors font-semibold py-2 border-b border-gray-100"
                onClick={() => setMobileMenuOpen(false)}
                data-testid="mobile-nav-perspectivas"
              >
                Perspectivas
              </a>
              <a 
                href="/app-asofom" 
                className="block text-[#1B3A5F] hover:text-[#FF6B35] transition-colors font-semibold py-2 border-b border-gray-100"
                onClick={() => setMobileMenuOpen(false)}
                data-testid="landing-mobile-nav-app"
              >
                📱 App ASOFOM
              </a>
              <a 
                href="#sponsors" 
                className="block text-[#1B3A5F] hover:text-[#FF6B35] transition-colors font-semibold py-2 border-b border-gray-100"
                onClick={() => setMobileMenuOpen(false)}
              >
                {language === 'es' ? 'Patrocinadores' : 'Sponsors'}
              </a>
              <Link
                to="/patrocinios"
                className="block text-[#FF6B35] hover:text-[#E55A28] transition-colors font-bold py-2 border-b border-gray-100"
                onClick={() => setMobileMenuOpen(false)}
                data-testid="landing-nav-patrocinios-mobile"
              >
                {language === 'es' ? '✨ Patrocinar 2027' : '✨ Sponsor 2027'}
              </Link>
              <Button 
                asChild
                data-testid="landing-mobile-2027-btn"
                className="w-full bg-[#FF6B35] hover:bg-[#E55A28] text-white font-semibold py-3 mt-2"
              >
                <a href={PRE_REGISTRO_2027_URL} target="_blank" rel="noopener noreferrer" onClick={() => { trackPreregistroClick(); setMobileMenuOpen(false); }}>
                  <Flame className="w-4 h-4 mr-1.5" />
                  {language === 'es' ? 'Pre-regístrate 2027' : 'Pre-register 2027'}
                </a>
              </Button>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section with Aftermovie */}
      <section className="relative pt-20 min-h-screen flex items-center overflow-hidden" data-testid="hero-section">
        {/* Hero background: event photo on mobile (LCP), aftermovie loop on desktop */}
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.weserv.nl/?url=customer-assets-lxgj4vgw.emergentagent.net/job_d4b3d2e1-67da-40cf-8efa-61dbe3f79433/artifacts/kcrd7vym_WhatsApp%20Image%202026-08-29%20at%205.57.30%20AM.jpeg&w=1600&q=70&output=webp"
            alt=""
            aria-hidden="true"
            width="1600"
            height="1067"
            fetchPriority="high"
            decoding="async"
            className="absolute inset-0 w-full h-full object-cover"
          />
          <iframe
            className="hidden md:block absolute top-1/2 left-1/2 w-[200vw] h-[200vh] -translate-x-1/2 -translate-y-1/2 pointer-events-none"
            src={`https://www.youtube-nocookie.com/embed/${AFTERMOVIE_ID}?autoplay=1&mute=1&loop=1&playlist=${AFTERMOVIE_ID}&controls=0&showinfo=0&rel=0&modestbranding=1&playsinline=1`}
            title="Aftermovie 20ª Convención Nacional ASOFOM"
            allow="autoplay; encrypted-media"
            loading="lazy"
            data-testid="hero-aftermovie-bg"
          ></iframe>
          <div className="absolute inset-0 bg-gradient-to-r from-[#071528]/90 via-[#071528]/70 to-[#071528]/30"></div>
          <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-[#071528] to-transparent"></div>
        </div>

        {/* "20" massive numeral watermark */}
        <div aria-hidden="true" className="absolute right-[-4%] bottom-[-14%] z-[1] select-none pointer-events-none leading-none font-black text-white/[0.06] text-[24rem] md:text-[42rem]">20</div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
          <div className="max-w-4xl">
            <div className="flex flex-wrap items-center gap-2 mb-6">
              <Badge className="bg-[#D4AF37] text-[#1B3A5F] font-bold text-sm px-4 py-2 border-0" data-testid="hero-success-badge">
                <Star className="w-4 h-4 mr-2 inline" />
                {language === 'es' ? '20° Convención Nacional · Concluida con éxito' : '20th National Convention · Successfully concluded'}
              </Badge>
              <Badge className="bg-white/10 border border-white/25 text-white font-semibold text-xs px-3 py-2 backdrop-blur-sm hover:bg-white/10" data-testid="top3-badge">
                <Award className="w-3.5 h-3.5 mr-1.5 inline text-[#D4AF37]" />
                {language === 'es' ? 'Uno de los 3 eventos financieros más importantes de México' : "One of Mexico's 3 most important financial events"}
              </Badge>
            </div>
            
            <h1 className="text-5xl md:text-7xl font-bold text-white mb-6 leading-[1.05] animate-fade-in-up">
              {language === 'es' ? '¡GRACIAS por hacer historia!' : 'THANK YOU for making history!'}
              <span className="block bg-gradient-to-r from-[#FF6B35] via-[#FFB073] to-[#D4AF37] bg-clip-text text-transparent mt-3">
                {language === 'es' ? 'Nos vemos en 2027.' : 'See you in 2027.'}
              </span>
            </h1>
            
            <p className="text-xl md:text-2xl text-gray-200 mb-8 leading-relaxed max-w-3xl">
              {language === 'es'
                ? 'Tres días en Cancún, +800 líderes del financiamiento, +60 speakers y un Sold Out histórico. La edición 20 ya es leyenda: revive los mejores momentos y asegura tu lugar en la siguiente.'
                : 'Three days in Cancún, +800 financing leaders, +60 speakers and a historic Sold Out. Edition 20 is already legend: relive the best moments and secure your spot at the next one.'}
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 mb-12">
              <div className="flex items-center text-white bg-white/10 backdrop-blur-sm px-6 py-3 rounded-lg border border-white/20">
                <Calendar className="w-5 h-5 mr-3 text-[#FF6B35]" />
                <span className="font-semibold">{t('hero.date')}</span>
              </div>
              <div className="flex items-center text-white bg-white/10 backdrop-blur-sm px-6 py-3 rounded-lg border border-white/20">
                <MapPin className="w-5 h-5 mr-3 text-[#FF6B35]" />
                <span className="font-semibold">{t('hero.location')}</span>
              </div>
              <div className="flex items-center text-[#071528] bg-[#D4AF37] px-6 py-3 rounded-lg border border-[#D4AF37] font-black uppercase tracking-wider text-sm" data-testid="hero-soldout-pill">
                <Flame className="w-5 h-5 mr-3" />
                Sold Out
              </div>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-4">
              <Button 
                onClick={() => scrollTo('gracias')}
                size="lg"
                data-testid="hero-aftermovie-btn"
                className="reservar-cta bg-[#FF6B35] hover:bg-[#E55A28] text-white font-bold px-8 py-6 text-lg shadow-2xl hover:shadow-[#FF6B35]/50 transition-all"
              >
                <Play className="mr-2 w-5 h-5 fill-white" />
                {language === 'es' ? 'Ver aftermovie y galería' : 'Watch aftermovie & gallery'}
              </Button>
              <Button 
                asChild
                size="lg"
                variant="outline"
                data-testid="hero-2027-btn"
                className="border-2 border-[#D4AF37] bg-[#D4AF37]/15 text-white hover:bg-[#D4AF37] hover:text-[#071528] font-bold px-8 py-6 text-lg backdrop-blur-sm"
              >
                <a href={PRE_REGISTRO_2027_URL} target="_blank" rel="noopener noreferrer" onClick={trackPreregistroClick}>
                  <Flame className="mr-2 w-5 h-5" />
                  {language === 'es' ? 'Pre-regístrate para 2027' : 'Pre-register for 2027'}
                </a>
              </Button>
            </div>
            <div className="mt-6" data-testid="hero-counter-wrap">
              <PreregistroCounter />
            </div>
          </div>
        </div>
      </section>

      {/* Post-event thank you + gallery */}
      <PostEventSection />

      {/* App ASOFOM mini banner */}
      <AppPromoSection />


      {/* Speakers Section */}
      <SpeakersSection variant="landing" />

      {/* Sponsors Section */}
      <section id="sponsors" className="py-24 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <Badge className="mb-4 bg-[#D4AF37]/20 text-[#1B3A5F] font-semibold border-0 px-4 py-2">
              {t('sponsors.badge')}
            </Badge>
            <h2 className="text-4xl md:text-5xl font-bold text-[#1B3A5F] mb-6">
              {t('sponsors.title')}
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              {t('sponsors.subtitle')}
            </p>
            
            {/* Sponsorship thank-you */}
            <div className="mt-8 bg-gradient-to-r from-[#1B3A5F]/5 via-[#D4AF37]/10 to-[#1B3A5F]/5 rounded-2xl p-8 max-w-4xl mx-auto" data-testid="landing-sponsors-thanks">
              <p className="text-lg font-semibold text-[#1B3A5F] mb-3">
                {language === 'es' 
                  ? 'A cada una de las +60 marcas que confiaron en la 20ª Convención: GRACIAS.'
                  : 'To each of the +60 brands that trusted the 20th Convention: THANK YOU.'}
              </p>
              <p className="text-gray-600 mb-6">
                {language === 'es'
                  ? 'Su respaldo hizo posible un Sold Out histórico y tres días que marcaron al sector. La 21ª edición ya está en camino y los espacios de patrocinio se asignan por orden de lista de espera.'
                  : 'Your support made a historic Sold Out possible and three days that marked the sector. The 21st edition is on its way and sponsorship spaces are assigned by waitlist order.'}
              </p>
              <div className="flex flex-wrap items-center justify-center gap-3">
                <Link
                  to="/patrocinios"
                  className="reservar-cta inline-flex items-center gap-2 bg-[#FF6B35] hover:bg-[#E55A28] text-white font-bold px-6 py-3 rounded-full shadow-lg"
                  data-testid="landing-sponsors-explore-cta"
                >
                  <Sparkles className="w-4 h-4" />
                  {language === 'es' ? 'Patrocinar la edición 2027' : 'Sponsor the 2027 edition'}
                  <ChevronRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          </div>
          
          {/* Sponsor Categories */}
          <div className="space-y-12">
            {/* Titanium Category */}
            <div className="text-center">
              <div className="inline-flex items-center gap-2 bg-gradient-to-r from-slate-600 via-slate-400 to-slate-600 text-white px-6 py-2 rounded-full mb-6">
                <Trophy className="w-5 h-5" />
                <span className="font-bold tracking-wider">TITANIUM</span>
              </div>
              <div className="flex justify-center">
                <a href="https://peibo.mx/" target="_blank" rel="noopener noreferrer" className="group">
                  <div className="bg-white rounded-2xl p-6 shadow-lg border-2 border-slate-200 hover:shadow-xl hover:border-slate-400 transition-all duration-300">
                    <img 
                      src="https://images.weserv.nl/?url=customer-assets.emergentagent.com/job_d4b3d2e1-67da-40cf-8efa-61dbe3f79433/artifacts/3idy5kyf_Logo%20peibo.webp&w=400&output=webp&q=75"
                      alt="Peibo - Patrocinador Titanium"
                      className="h-16 w-auto object-contain"
                      loading="lazy"
                    />
                    <div className="flex items-center justify-center gap-1 mt-2 text-xs text-gray-400 group-hover:text-[#1B3A5F]">
                      <ExternalLink className="w-3 h-3" />
                      <span>peibo.mx</span>
                    </div>
                  </div>
                </a>
              </div>
            </div>
            
            {/* Oro Category */}
            <div className="text-center">
              <div className="inline-flex items-center gap-2 bg-gradient-to-r from-[#D4AF37] via-[#F5D87A] to-[#D4AF37] text-[#1B3A5F] px-6 py-2 rounded-full mb-6">
                <Star className="w-5 h-5" />
                <span className="font-bold tracking-wider">ORO</span>
              </div>
              <div className="flex justify-center">
                <a href="https://www.circulodecredito.com.mx/home" target="_blank" rel="noopener noreferrer" className="group">
                  <div className="bg-white rounded-2xl p-6 shadow-lg border-2 border-[#D4AF37]/30 hover:shadow-xl hover:border-[#D4AF37] transition-all duration-300">
                    <img 
                      src="https://images.weserv.nl/?url=customer-assets.emergentagent.com/job_d4b3d2e1-67da-40cf-8efa-61dbe3f79433/artifacts/03m93j7p_Circulo%20de%20cre%CC%81dito.png&w=400&output=webp&q=75"
                      alt="Círculo de Crédito - Patrocinador Oro"
                      className="h-12 w-auto object-contain"
                      loading="lazy"
                    />
                    <div className="flex items-center justify-center gap-1 mt-2 text-xs text-gray-400 group-hover:text-[#D4AF37]">
                      <ExternalLink className="w-3 h-3" />
                      <span>circulodecredito.com.mx</span>
                    </div>
                  </div>
                </a>
              </div>
            </div>
            
            {/* Plata Category */}
            <div className="text-center">
              <div className="inline-flex items-center gap-2 bg-gradient-to-r from-gray-400 via-gray-200 to-gray-400 text-gray-700 px-6 py-2 rounded-full mb-6">
                <Award className="w-5 h-5" />
                <span className="font-bold tracking-wider">PLATA</span>
              </div>
              <div className="flex flex-wrap justify-center gap-4">
                <a href="https://syntage.com/" target="_blank" rel="noopener noreferrer" className="group">
                  <div className="bg-white rounded-xl p-5 shadow-lg border-2 border-gray-200 hover:shadow-xl hover:border-gray-400 transition-all duration-300 flex items-center justify-center h-24 w-40">
                    <img 
                      src="https://images.weserv.nl/?url=customer-assets.emergentagent.com/job_d4b3d2e1-67da-40cf-8efa-61dbe3f79433/artifacts/0xjlid87_Syntage%20logo.svg&w=400&output=webp&q=75"
                      alt="Syntage - Patrocinador Plata"
                      className="w-full h-full object-contain"
                      loading="lazy"
                    />
                  </div>
                  <div className="flex items-center justify-center gap-1 mt-2 text-xs text-gray-400 group-hover:text-gray-600">
                    <ExternalLink className="w-3 h-3" />
                    <span>syntage.com</span>
                  </div>
                </a>
                <a href="https://preven.mx/" target="_blank" rel="noopener noreferrer" className="group">
                  <div className="bg-white rounded-xl p-5 shadow-lg border-2 border-gray-200 hover:shadow-xl hover:border-gray-400 transition-all duration-300 flex items-center justify-center h-24 w-40">
                    <img 
                      src="https://images.weserv.nl/?url=customer-assets.emergentagent.com/job_d4b3d2e1-67da-40cf-8efa-61dbe3f79433/artifacts/4npwoj90_logo%20preven%20patrocinador%20ASOFOM.png&w=400&output=webp&q=75"
                      alt="Preven - Patrocinador Plata"
                      className="w-full h-full object-contain"
                    />
                  </div>
                  <div className="flex items-center justify-center gap-1 mt-2 text-xs text-gray-400 group-hover:text-gray-600">
                    <ExternalLink className="w-3 h-3" />
                    <span>preven.mx</span>
                  </div>
                </a>
                <a href="https://www.stonex.com/en/" target="_blank" rel="noopener noreferrer" className="group">
                  <div className="bg-white rounded-xl p-5 shadow-lg border-2 border-gray-200 hover:shadow-xl hover:border-gray-400 transition-all duration-300 flex items-center justify-center h-24 w-40">
                    <img 
                      src="https://images.weserv.nl/?url=customer-assets-lxgj4vgw.emergentagent.net/job_d4b3d2e1-67da-40cf-8efa-61dbe3f79433/artifacts/lf0nf15i_Stonex.webp&w=400&output=webp&q=75"
                      alt="StoneX - Patrocinador Plata"
                      className="w-full h-full object-contain"
                      loading="lazy"
                    />
                  </div>
                  <div className="flex items-center justify-center gap-1 mt-2 text-xs text-gray-400 group-hover:text-gray-600">
                    <ExternalLink className="w-3 h-3" />
                    <span>stonex.com</span>
                  </div>
                </a>
                <a href="https://www.finbeabc.com.mx/" target="_blank" rel="noopener noreferrer" className="group" data-testid="sponsor-finbe-abc">
                  <div className="bg-white rounded-xl p-5 shadow-lg border-2 border-gray-200 hover:shadow-xl hover:border-gray-400 transition-all duration-300 flex items-center justify-center h-24 w-40">
                    <img 
                      src="https://images.weserv.nl/?url=customer-assets-lxgj4vgw.emergentagent.net/job_d4b3d2e1-67da-40cf-8efa-61dbe3f79433/artifacts/2o3pb4a2_Finbe%20asofom%20logo.png&trim=10&w=400&output=webp&q=75"
                      alt="FinBe ABC - Patrocinador Plata"
                      className="w-full h-full object-contain"
                      loading="lazy"
                    />
                  </div>
                  <div className="flex items-center justify-center gap-1 mt-2 text-xs text-gray-400 group-hover:text-gray-600">
                    <ExternalLink className="w-3 h-3" />
                    <span>finbeabc.com.mx</span>
                  </div>
                </a>
              </div>
            </div>
            
            {/* Bronce Especial Category */}
            <div className="text-center">
              <div className="inline-flex items-center gap-2 bg-gradient-to-r from-amber-700 via-amber-500 to-amber-700 text-white px-6 py-2 rounded-full mb-6">
                <Award className="w-5 h-5" />
                <span className="font-bold tracking-wider">BRONCE ESPECIAL</span>
              </div>
              <div className="flex flex-wrap justify-center gap-4">
                <a href="https://www.burodecredito.com.mx/" target="_blank" rel="noopener noreferrer" className="group">
                  <div className="bg-white rounded-xl p-4 shadow-lg border-2 border-amber-200 hover:shadow-xl hover:border-amber-400 transition-all duration-300 flex items-center justify-center h-20 w-36">
                    <img 
                      src="https://images.weserv.nl/?url=customer-assets.emergentagent.com/job_d4b3d2e1-67da-40cf-8efa-61dbe3f79433/artifacts/2xzww3ve_BdC-TU-vert-logo-cmyk%20%281%29.jpg&w=400&output=webp&q=75"
                      alt="Buró de Crédito - Patrocinador Bronce Especial"
                      className="w-full h-full object-contain"
                      loading="lazy"
                      onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling && (e.target.nextSibling.style.display = 'block'); }}
                    />
                    <span className="text-xs font-bold text-[#1B3A5F] text-center hidden">Buró de Crédito</span>
                  </div>
                  <div className="flex items-center justify-center gap-1 mt-2 text-xs text-gray-400 group-hover:text-amber-600">
                    <ExternalLink className="w-3 h-3" />
                    <span>burodecredito.com.mx</span>
                  </div>
                </a>
                <a href="https://www.intelligential.tech/" target="_blank" rel="noopener noreferrer" className="group">
                  <div className="bg-white rounded-xl p-4 shadow-lg border-2 border-amber-200 hover:shadow-xl hover:border-amber-400 transition-all duration-300 flex items-center justify-center h-20 w-36">
                    <img 
                      src={`${process.env.REACT_APP_BACKEND_URL}/api/logos/intelligential`}
                      alt="Intelligential - Patrocinador Bronce Especial"
                      className="w-full h-full object-contain"
                      loading="lazy"
                      onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling && (e.target.nextSibling.style.display = 'block'); }}
                    />
                    <span className="text-xs font-bold text-[#1B3A5F] text-center hidden">Intelligential</span>
                  </div>
                  <div className="flex items-center justify-center gap-1 mt-2 text-xs text-gray-400 group-hover:text-amber-600">
                    <ExternalLink className="w-3 h-3" />
                    <span>intelligential.tech</span>
                  </div>
                </a>
                <a href="https://moonflow.club/" target="_blank" rel="noopener noreferrer" className="group">
                  <div className="bg-white rounded-xl p-4 shadow-lg border-2 border-amber-200 hover:shadow-xl hover:border-amber-400 transition-all duration-300 flex items-center justify-center h-20 w-36">
                    <img 
                      src="https://images.weserv.nl/?url=customer-assets.emergentagent.com/job_d4b3d2e1-67da-40cf-8efa-61dbe3f79433/artifacts/h1ik34yv_Moonflow%20convencio%CC%81n%20asofom%202026.png&w=400&output=webp&q=75"
                      alt="Moonflow - Patrocinador Bronce Especial"
                      className="w-full h-full object-contain"
                      loading="lazy"
                      onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling && (e.target.nextSibling.style.display = 'block'); }}
                    />
                    <span className="text-xs font-bold text-[#1B3A5F] text-center hidden">Moonflow</span>
                  </div>
                  <div className="flex items-center justify-center gap-1 mt-2 text-xs text-gray-400 group-hover:text-amber-600">
                    <ExternalLink className="w-3 h-3" />
                    <span>moonflow.club</span>
                  </div>
                </a>
              </div>
            </div>
            
            {/* Bronce Category */}
            <div className="text-center">
              <div className="inline-flex items-center gap-2 bg-gradient-to-r from-amber-800 via-amber-600 to-amber-800 text-white px-6 py-2 rounded-full mb-6">
                <Award className="w-5 h-5" />
                <span className="font-bold tracking-wider">BRONCE</span>
              </div>
              <div className="flex flex-wrap justify-center gap-4">
                {[
                  { name: 'LENDIA', url: 'https://www.lendia.mx/es', logo: `${process.env.REACT_APP_BACKEND_URL}/api/logos/lendia` },
                  { name: 'AML', url: 'https://www.amlconsultoria.com/', logo: 'https://images.weserv.nl/?url=customer-assets.emergentagent.com/job_d4b3d2e1-67da-40cf-8efa-61dbe3f79433/artifacts/94272mah_Logo%20AML%20Patrocinador%20ASOFOM.png&w=400&output=webp&q=75' },
                  { name: 'SIAC', url: 'https://siac.com.mx/', logo: 'https://images.weserv.nl/?url=customer-assets.emergentagent.com/job_d4b3d2e1-67da-40cf-8efa-61dbe3f79433/artifacts/l0j1r19e_logo%20SIAC%20Patrocinador%20ASOFOM.png&w=400&output=webp&q=75' },
                  { name: 'VERUM', url: 'https://pcrverum.mx/', logo: `${process.env.REACT_APP_BACKEND_URL}/api/logos/verum` },
                  { name: 'HR RATINGS', url: 'https://www.hrratings.com/', logo: `${process.env.REACT_APP_BACKEND_URL}/api/logos/hr_ratings` },
                  { name: "MOODY'S", url: 'https://moodyslocal.com.mx/', logo: `${process.env.REACT_APP_BACKEND_URL}/api/logos/moodys` },
                  { name: 'CLICK SEGURIDAD JURÍDICA', url: 'https://clickseguridad.com/', logo: `${process.env.REACT_APP_BACKEND_URL}/api/logos/click_seguridad` },
                  { name: 'CROWE', url: 'https://www.crowe.com/mx', logo: 'https://images.weserv.nl/?url=customer-assets.emergentagent.com/job_d4b3d2e1-67da-40cf-8efa-61dbe3f79433/artifacts/7ghcrniw_Crowe%20patrocinador%20ASOFOM%20Convencio%CC%81n%20Nacional.png&w=400&output=webp&q=75' },
                  { name: 'DYNAMICORE', url: 'https://dynamicore.io/', logo: 'https://images.weserv.nl/?url=customer-assets.emergentagent.com/job_d4b3d2e1-67da-40cf-8efa-61dbe3f79433/artifacts/kw9b9gkb_logo%20vertical%402x.png&w=400&output=webp&q=75' },
                  { name: 'FINTOC', url: 'https://www.fintoc.com/cl', logo: 'https://images.weserv.nl/?url=customer-assets-lxgj4vgw.emergentagent.net/job_d4b3d2e1-67da-40cf-8efa-61dbe3f79433/artifacts/ro7ngpu9_Logo%20fintoc%20negro%20%281%29.png&trim=10&w=400&output=webp&q=75' }
                ].map((sponsor) => (
                  <a key={sponsor.name} href={sponsor.url} target="_blank" rel="noopener noreferrer" className="group">
                    <div className="bg-white rounded-lg p-3 shadow border-2 border-amber-100 hover:shadow-lg hover:border-amber-300 transition-all duration-300 flex items-center justify-center h-16 w-32">
                      {sponsor.logo ? (
                        <>
                          <img 
                            src={sponsor.logo}
                            alt={`${sponsor.name} - Patrocinador Bronce`}
                            className="w-full h-full object-contain"
                            onError={(e) => {
                              e.target.style.display = 'none';
                              e.target.nextSibling && (e.target.nextSibling.style.display = 'block');
                            }}
                          />
                          <span className="text-xs font-semibold text-[#1B3A5F] text-center hidden">
                            {sponsor.name}
                          </span>
                        </>
                      ) : (
                        <span className="text-xs font-semibold text-[#1B3A5F] text-center">
                          {sponsor.name}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center justify-center gap-1 mt-1 text-xs text-gray-300 group-hover:text-amber-500">
                      <ExternalLink className="w-3 h-3" />
                    </div>
                  </a>
                ))}
              </div>
            </div>
            
            {/* Convención Category */}
            <div className="text-center">
              <div className="inline-flex items-center gap-2 bg-gradient-to-r from-[#1B3A5F] via-[#2A4A6F] to-[#1B3A5F] text-white px-6 py-2 rounded-full mb-6">
                <Handshake className="w-5 h-5" />
                <span className="font-bold tracking-wider">CONVENCIÓN</span>
              </div>
              <div className="flex flex-wrap justify-center gap-4">
                {[
                  { name: 'FIMUBAC', url: 'https://credenz.com.mx/nuestros-productos/lineas-de-fondeo/', logo: `${process.env.REACT_APP_BACKEND_URL}/api/logos/fimubac` },
                  { name: 'QUANTO', url: 'https://quanto.mx/', logo: `${process.env.REACT_APP_BACKEND_URL}/api/logos/quanto` },
                  { name: 'LOGIC SYSTEMS', url: 'https://logicsystems.com.mx/', logo: `${process.env.REACT_APP_BACKEND_URL}/api/logos/logic_systems` },
                  { name: 'TAPI', url: 'https://www.tapipay.la/', logo: `${process.env.REACT_APP_BACKEND_URL}/api/logos/tapi` },
                  { name: 'HILCO GLOBAL MEXICO', url: 'https://www.hilcoglobal.mx/', logo: `${process.env.REACT_APP_BACKEND_URL}/api/logos/hilco_global` },
                  { name: 'I-STRUCTURE', url: 'https://www.i-structure.com.mx/en/inicio-en/', logo: `${process.env.REACT_APP_BACKEND_URL}/api/logos/i_structure` },
                  { name: 'HITO SERVICING', url: 'https://www.hitoservicing.com/', logo: 'https://images.weserv.nl/?url=customer-assets.emergentagent.com/job_d4b3d2e1-67da-40cf-8efa-61dbe3f79433/artifacts/bqlts06v_HITO.png&w=400&output=webp&q=75' },
                  { name: 'FITCH RATINGS', url: 'https://www.fitchratings.com/', logo: 'https://images.weserv.nl/?url=customer-assets.emergentagent.com/job_d4b3d2e1-67da-40cf-8efa-61dbe3f79433/artifacts/olmomb0k_Fitch%20Ratings%20ASOFOM%20convencio%CC%81n%20nacional%202026.svg&w=400&output=webp&q=75' },
                  { name: 'GRAIN', url: 'https://www.grainfinance.com/', logo: 'https://customer-assets.emergentagent.com/job_d4b3d2e1-67da-40cf-8efa-61dbe3f79433/artifacts/ftvrfgvs_Blue%20%2B%20Symbol%20%283%29%20%281%29%20%281%29.png' },
                  { name: 'JAPIFON', url: 'https://japifon.com/', logo: 'https://images.weserv.nl/?url=customer-assets.emergentagent.com/job_d4b3d2e1-67da-40cf-8efa-61dbe3f79433/artifacts/8ctirzvl_JAPIFON.png&w=400&output=webp&q=75' },
                  { name: 'KPITALIA', url: 'https://kpitalia.com/investmentbanking/', logo: 'https://images.weserv.nl/?url=customer-assets.emergentagent.com/job_d4b3d2e1-67da-40cf-8efa-61dbe3f79433/artifacts/ewfxo83q_Kpitalia.png&w=400&output=webp&q=75' },
                  { name: 'FS ADVISORY', url: 'https://fs-advisory.com/', logo: 'https://images.weserv.nl/?url=customer-assets.emergentagent.com/job_d4b3d2e1-67da-40cf-8efa-61dbe3f79433/artifacts/5a10j8co_FS%20Advisory.png&trim=20&w=400&output=webp&q=75' },
                  { name: 'SURA', url: 'https://www.segurossura.com.mx/', logo: 'https://images.weserv.nl/?url=customer-assets.emergentagent.com/job_d4b3d2e1-67da-40cf-8efa-61dbe3f79433/artifacts/0m6izn0y_SURA_SEGUROS_logotipo_unatinta_azul_positivo.png&trim=20&w=400&output=webp&q=75' },
                  { name: 'KANAN', url: 'https://kananservicios.com', logo: 'https://images.weserv.nl/?url=customer-assets.emergentagent.com/job_d4b3d2e1-67da-40cf-8efa-61dbe3f79433/artifacts/7e82rzsl_26.%20Kanan.png&trim=20&w=400&output=webp&q=75' },
                  { name: 'MONATO', url: 'https://www.monato.com/', logo: 'https://images.weserv.nl/?url=customer-assets.emergentagent.com/job_d4b3d2e1-67da-40cf-8efa-61dbe3f79433/artifacts/ox75gynh_Montano.webp&trim=20&w=400&output=webp&q=75' },
                  { name: 'BRIDGE HORIZON', url: 'https://www.bridge.mx/', logo: 'https://images.weserv.nl/?url=customer-assets.emergentagent.com/job_d4b3d2e1-67da-40cf-8efa-61dbe3f79433/artifacts/rn2o3gpf_Bridge%20Horizon.webp&trim=20&w=400&output=webp&q=75' },
                  { name: 'PAYNAU', url: 'https://www.paynau.com/es/', logo: 'https://images.weserv.nl/?url=customer-assets.emergentagent.com/job_d4b3d2e1-67da-40cf-8efa-61dbe3f79433/artifacts/hcb192q4_Paynau_2024_RGB_Black.png&trim=20&w=400&output=webp&q=75' },
                  { name: 'FINASIST', url: 'https://www.finasist.com/', logo: 'https://images.weserv.nl/?url=customer-assets.emergentagent.com/job_d4b3d2e1-67da-40cf-8efa-61dbe3f79433/artifacts/m7s30697_Finasist.png&trim=20&w=400&output=webp&q=75' },
                  { name: 'MAAT.IA', url: 'https://www.maatai.com/', logo: 'https://images.weserv.nl/?url=customer-assets.emergentagent.com/job_d4b3d2e1-67da-40cf-8efa-61dbe3f79433/artifacts/xbfqdhxr_maat-logo-blue.png&trim=20&w=400&output=webp&q=75' },
                  { name: 'FIMPE', url: 'https://fimpe.org/', logo: 'https://images.weserv.nl/?url=customer-assets.emergentagent.com/job_d4b3d2e1-67da-40cf-8efa-61dbe3f79433/artifacts/mp8fwj92_Fimpe.png&trim=20&w=400&output=webp&q=75' },
                  { name: 'NUMARIS', url: 'https://www.numaris.com/', logo: 'https://images.weserv.nl/?url=customer-assets-lxgj4vgw.emergentagent.net/job_d4b3d2e1-67da-40cf-8efa-61dbe3f79433/artifacts/ej68octn_Numarius%20logo.webp&w=400&output=webp&q=75' },
                  { name: 'JELOU', url: 'https://jelou.ai/es/', logo: 'https://images.weserv.nl/?url=customer-assets-lxgj4vgw.emergentagent.net/job_d4b3d2e1-67da-40cf-8efa-61dbe3f79433/artifacts/morigi05_1.%20Logo-Jelou-Principal.png&w=400&output=webp&q=75' },
                  { name: 'LINEDATA', url: 'https://www.linedata.com/', logo: 'https://images.weserv.nl/?url=customer-assets-lxgj4vgw.emergentagent.net/job_d4b3d2e1-67da-40cf-8efa-61dbe3f79433/artifacts/hnxppvrt_Linedata_Logo_RVB.jpg.webp&trim=10&w=400&output=webp&q=75' },
                  { name: 'APPROVAL', url: 'https://www.grupocsi.com/art', logo: 'https://images.weserv.nl/?url=customer-assets-lxgj4vgw.emergentagent.net/job_d4b3d2e1-67da-40cf-8efa-61dbe3f79433/artifacts/1jt4n07w_APPROVAL%20IN%20REAL%20TIME.png&trim=10&w=400&output=webp&q=75' },
                  { name: 'PERSONA', url: 'https://withpersona.com/', logo: 'https://images.weserv.nl/?url=customer-assets-lxgj4vgw.emergentagent.net/job_d4b3d2e1-67da-40cf-8efa-61dbe3f79433/artifacts/x3k4k7bq_Persona.png&w=400&output=webp&q=75' },
                  { name: 'ASP INTEGRA OPCIONES', url: 'https://www.aspintegraopciones.com/fr/new/home/', logo: 'https://images.weserv.nl/?url=customer-assets-lxgj4vgw.emergentagent.net/job_d4b3d2e1-67da-40cf-8efa-61dbe3f79433/artifacts/gtttkd4n_ASP%20Integra%20loog.png&trim=10&w=400&output=webp&q=75' },
                  { name: 'NUBARIUM', url: 'https://nubarium.com/', logo: 'https://images.weserv.nl/?url=customer-assets-lxgj4vgw.emergentagent.net/job_d4b3d2e1-67da-40cf-8efa-61dbe3f79433/artifacts/wpmoohqg_Nubarium.webp&trim=10&w=400&output=webp&q=75' },
                  { name: 'CREDITRUST', url: 'https://www.creditrust.mx/home', logo: 'https://images.weserv.nl/?url=customer-assets-lxgj4vgw.emergentagent.net/job_d4b3d2e1-67da-40cf-8efa-61dbe3f79433/artifacts/qp5gs5fe_28.%20Creditrust.png&trim=10&w=400&output=webp&q=75' },
                  { name: 'PAGATODO', url: 'https://www.pagatodo.com/', logo: 'https://images.weserv.nl/?url=customer-assets-lxgj4vgw.emergentagent.net/job_d4b3d2e1-67da-40cf-8efa-61dbe3f79433/artifacts/slgknclj_Pagatodo.png&trim=10&w=400&output=webp&q=75' },
                  { name: 'WE.LEGAL SUITE', url: 'https://welegal.mx/?utm_source=ig&utm_medium=social&utm_content=link_in_bio', logo: 'https://images.weserv.nl/?url=customer-assets-lxgj4vgw.emergentagent.net/job_d4b3d2e1-67da-40cf-8efa-61dbe3f79433/artifacts/4e56bt8d_LOGO%20WE%20LEGAL%20SUITE.png&trim=10&w=400&output=webp&q=75' }
                ].map((sponsor) => (
                  <a key={sponsor.name} href={sponsor.url} target="_blank" rel="noopener noreferrer" className="group">
                    <div className="bg-white rounded-lg p-3 shadow border-2 border-[#1B3A5F]/20 hover:shadow-lg hover:border-[#1B3A5F]/50 transition-all duration-300 flex items-center justify-center h-16 w-32">
                      {sponsor.logo ? (
                        <>
                          <img 
                            src={sponsor.logo}
                            alt={`${sponsor.name} - Patrocinador Convención`}
                            className="w-full h-full object-contain"
                            loading="lazy"
                            onError={(e) => {
                              e.target.style.display = 'none';
                              e.target.nextSibling && (e.target.nextSibling.style.display = 'block');
                            }}
                          />
                          <span className="text-xs font-semibold text-[#1B3A5F] text-center hidden">
                            {sponsor.name}
                          </span>
                        </>
                      ) : (
                        <span className="text-xs font-semibold text-[#1B3A5F] text-center">
                          {sponsor.name}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center justify-center gap-1 mt-1 text-xs text-gray-300 group-hover:text-[#1B3A5F]">
                      <ExternalLink className="w-3 h-3" />
                    </div>
                  </a>
                ))}
              </div>
            </div>

            {/* Fondeadores Category */}
            <div className="text-center">
              <div className="inline-flex items-center gap-2 bg-gradient-to-r from-emerald-700 via-emerald-500 to-emerald-700 text-white px-6 py-2 rounded-full mb-6">
                <Handshake className="w-5 h-5" />
                <span className="font-bold tracking-wider">{language === 'es' ? 'FONDEADORES' : 'FUNDERS'}</span>
              </div>
              <div className="flex flex-wrap justify-center gap-4">
                {[
                  { name: 'ALLOY CAPITAL', url: 'https://www.alloylp.com/', logo: 'https://images.weserv.nl/?url=customer-assets.emergentagent.com/job_d4b3d2e1-67da-40cf-8efa-61dbe3f79433/artifacts/vz6k1qp5_Alloy%20Capital.jpg&trim=20&w=400&output=webp&q=75' },
                  { name: 'GRUPO BMV', url: 'https://www.bmv.com.mx/', logo: 'https://images.weserv.nl/?url=customer-assets.emergentagent.com/job_d4b3d2e1-67da-40cf-8efa-61dbe3f79433/artifacts/4e7lr90u_Grupo%20Bolsa%20Mexicana%20de%20Valores.png&trim=20&w=400&output=webp&q=75' },
                  { name: 'BIVA', url: 'https://www.biva.mx/', logo: 'https://images.weserv.nl/?url=customer-assets.emergentagent.com/job_d4b3d2e1-67da-40cf-8efa-61dbe3f79433/artifacts/tgdol8cl_BIVA.svg&w=400&output=webp&q=75' },
                  { name: 'SFI', url: 'https://sfisofom.com.mx/#', logo: 'https://images.weserv.nl/?url=customer-assets.emergentagent.com/job_d4b3d2e1-67da-40cf-8efa-61dbe3f79433/artifacts/frci7u6u_SFI.png&w=400&output=webp&q=75' },
                  { name: 'PROMECAP', url: 'https://promecap.com.mx/esp/', logo: 'https://images.weserv.nl/?url=customer-assets.emergentagent.com/job_d4b3d2e1-67da-40cf-8efa-61dbe3f79433/artifacts/7d65ztck_logo_promecap.png&trim=20&w=400&output=webp&q=75' },
                  { name: 'BANVERDE', url: 'https://www.banverde.com/', logo: 'https://images.weserv.nl/?url=customer-assets.emergentagent.com/job_d4b3d2e1-67da-40cf-8efa-61dbe3f79433/artifacts/c4mlkrvr_Banverde_ih_26b27d%20%281%29.png&trim=20&w=400&output=webp&q=75' },
                  { name: 'COVALTO', url: 'https://www.covalto.com/', logo: 'https://images.weserv.nl/?url=customer-assets.emergentagent.com/job_d4b3d2e1-67da-40cf-8efa-61dbe3f79433/artifacts/wffh24gc_Main%20Logo.webp&trim=20&w=400&output=webp&q=75' },
                  { name: 'MULTIVA', url: 'https://www.multiva.com.mx/', logo: 'https://images.weserv.nl/?url=customer-assets-lxgj4vgw.emergentagent.net/job_d4b3d2e1-67da-40cf-8efa-61dbe3f79433/artifacts/j5crulib_AF_LogoMultiva_VP_POS_RGB.png&w=400&output=webp&q=75' },
                  { name: 'MINTOS', url: 'https://www.mintos.com/es/', logo: 'https://images.weserv.nl/?url=customer-assets-lxgj4vgw.emergentagent.net/job_d4b3d2e1-67da-40cf-8efa-61dbe3f79433/artifacts/gs5l36sb_mintos-logo.png&w=400&output=webp&q=75' },
                  { name: 'DEETKEN IMPACT', url: 'https://deetkenimpact.com/', logo: 'https://images.weserv.nl/?url=customer-assets-lxgj4vgw.emergentagent.net/job_d4b3d2e1-67da-40cf-8efa-61dbe3f79433/artifacts/ok8whj85_Final-Logo-large.webp&w=400&output=webp&q=75' },
                  { name: 'GK&O', url: 'https://www.gkoassociates.com/', logo: 'https://images.weserv.nl/?url=customer-assets-lxgj4vgw.emergentagent.net/job_d4b3d2e1-67da-40cf-8efa-61dbe3f79433/artifacts/9el367du_Logo%20gko.png&trim=10&w=400&output=webp&q=75' }
                ].map((sponsor) => (
                  <a key={sponsor.name} href={sponsor.url} target="_blank" rel="noopener noreferrer" className="group" data-testid={`sponsor-fondeador-${sponsor.name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
                    <div className="bg-white rounded-lg p-3 shadow border-2 border-emerald-200 hover:shadow-lg hover:border-emerald-400 transition-all duration-300 flex items-center justify-center h-16 w-32">
                      {sponsor.logo ? (
                        <>
                          <img
                            src={sponsor.logo}
                            alt={`${sponsor.name} - Fondeador`}
                            className="w-full h-full object-contain"
                            loading="lazy"
                            onError={(e) => {
                              e.target.style.display = 'none';
                              e.target.nextSibling && (e.target.nextSibling.style.display = 'block');
                            }}
                          />
                          <span className="text-xs font-semibold text-[#1B3A5F] text-center hidden">
                            {sponsor.name}
                          </span>
                        </>
                      ) : (
                        <span className="text-xs font-semibold text-[#1B3A5F] text-center">
                          {sponsor.name}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center justify-center gap-1 mt-1 text-xs text-gray-300 group-hover:text-emerald-600">
                      <ExternalLink className="w-3 h-3" />
                    </div>
                  </a>
                ))}
              </div>
            </div>
          </div>
          
          <div className="text-center mt-16">
            <div className="bg-gradient-to-r from-[#1B3A5F] to-[#2A4A6F] rounded-2xl p-8 max-w-2xl mx-auto shadow-xl">
              <p className="text-xl text-white font-semibold mb-6">
                {t('sponsors.interested')}
              </p>
              <Button 
                className="bg-[#FF6B35] hover:bg-[#E55A28] text-white font-bold px-6 sm:px-10 py-6 text-base sm:text-lg rounded-full shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300 whitespace-normal h-auto max-w-full"
                onClick={() => window.open(HUBSPOT_SPONSORSHIP_URL, '_blank')}
                data-testid="landing-sponsors-waitlist-btn"
              >
                <Mail className="mr-2 w-5 h-5" />
                {t('sponsors.contact')}
                <ChevronRight className="ml-2 w-5 h-5" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Strategic Partners Section */}
      <PartnersSection />

      {/* Perspectivas ASOFOM — Mini preview */}
      <section className="py-20 md:py-24 bg-gradient-to-b from-white via-[#FAFAF7] to-white" data-testid="home-perspectivas-preview">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
            <div className="max-w-2xl">
              <span className="inline-flex items-center gap-2 text-[#FF6B35] text-xs font-bold uppercase tracking-widest mb-3">
                <Sparkles className="w-3.5 h-3.5" />
                {language === 'es' ? 'Hub Editorial' : 'Editorial Hub'}
              </span>
              <h2 className="text-4xl md:text-5xl font-bold text-[#0B1E3D] tracking-tight leading-[1.1]">
                Perspectivas <span className="text-[#FF6B35]">ASOFOM</span>
              </h2>
              <p className="mt-4 text-gray-600 text-base md:text-lg leading-relaxed">
                {language === 'es'
                  ? 'Análisis, tendencias y conversaciones que están redefiniendo el futuro de las SOFOMes y del sistema financiero mexicano.'
                  : 'Analysis, trends and conversations redefining the future of SOFOMs and the Mexican financial system.'}
              </p>
            </div>
            <Button
              asChild
              size="lg"
              className="bg-[#0B1E3D] hover:bg-[#1B3A5F] text-white font-bold h-12 px-7 shadow-lg shrink-0"
              data-testid="home-perspectivas-cta"
            >
              <a href="/perspectivas">
                {language === 'es' ? 'Ver todas las perspectivas' : 'View all insights'}
                <ChevronRight className="w-4 h-4 ml-1" />
              </a>
            </Button>
          </div>

          <div className="grid md:grid-cols-3 gap-6 md:gap-8">
            {ARTICLES.slice(0, 3).map((a) => {
              const cat = getCategoryBySlug(a.categorySlug);
              return (
                <a
                  key={a.slug}
                  href={`/perspectivas/${a.slug}`}
                  className="group bg-white rounded-2xl overflow-hidden border border-gray-100 hover:shadow-2xl hover:-translate-y-1 transition-all duration-300"
                  data-testid={`home-perspectivas-card-${a.slug}`}
                >
                  <div className="relative aspect-[16/10] overflow-hidden">
                    <img
                      src={a.cover.image}
                      alt={a.cover.alt}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      loading="lazy"
                    />
                  </div>
                  <div className="p-6">
                    <span className="inline-block text-[10px] font-bold uppercase tracking-widest mb-3" style={{ color: cat?.color }}>
                      {cat?.label[language] || cat?.label.es}
                    </span>
                    <h3 className="font-bold text-[#0B1E3D] leading-snug text-lg md:text-xl group-hover:text-[#FF6B35] transition-colors">
                      {a.title[language] || a.title.es}
                    </h3>
                    <p className="mt-3 text-sm text-gray-600 leading-relaxed line-clamp-3">
                      {a.excerpt[language] || a.excerpt.es}
                    </p>
                    <div className="mt-5 flex items-center gap-4 text-[11px] text-gray-500 font-semibold">
                      <span>{new Date(a.publishedAt).toLocaleDateString(language === 'es' ? 'es-MX' : 'en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                      <span>· {a.readingMinutes} min</span>
                    </div>
                    <div className="mt-4 inline-flex items-center gap-1.5 text-[#FF6B35] text-sm font-bold group-hover:gap-2.5 transition-all">
                      {language === 'es' ? 'Leer perspectiva' : 'Read insight'}
                      <ChevronRight className="w-4 h-4" />
                    </div>
                  </div>
                </a>
              );
            })}
            {/* Placeholder card when fewer than 3 articles */}
            {ARTICLES.length < 3 && Array.from({ length: 3 - ARTICLES.length }).map((_, i) => (
              <div key={`placeholder-${i}`} className="relative bg-gradient-to-br from-[#FAFAF7] to-white border-2 border-dashed border-gray-200 rounded-2xl p-8 flex flex-col items-center justify-center text-center min-h-[300px]">
                <Sparkles className="w-8 h-8 text-[#D4AF37] mb-3 opacity-60" />
                <p className="font-bold text-[#0B1E3D] mb-2">{language === 'es' ? 'Próxima perspectiva' : 'Next insight'}</p>
                <p className="text-xs text-gray-500 max-w-[200px]">{language === 'es' ? 'Nuevos análisis cada semana rumbo a la Convención.' : 'New analysis every week on the road to the Convention.'}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pre-registro 21ª Convención 2027 */}
      <PreRegistro2027Section />

      {/* Footer - Updated */}
      <footer className="bg-[#0F1F35] text-gray-300 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-12 mb-12">
            <div className="col-span-2">
              <img 
                src="https://customer-assets.emergentagent.com/job_asofom-xx-summit/artifacts/rgcyz9ti_Logo%20Convencio%CC%81n%202026.png" 
                alt="20° Convención ASOFOM" 
                className="h-20 w-auto mb-6"
              />
              <p className="text-gray-400 leading-relaxed mb-6">
                {t('footer.description')}
              </p>
              <div className="flex space-x-4">
                <a href="https://www.linkedin.com/company/asofom" target="_blank" rel="noopener noreferrer"
                   className="w-10 h-10 bg-white/10 hover:bg-[#0077B5] rounded-full flex items-center justify-center transition-colors">
                  <Linkedin className="w-5 h-5" />
                </a>
                <a href="https://www.facebook.com/AsofomMx" target="_blank" rel="noopener noreferrer"
                   className="w-10 h-10 bg-white/10 hover:bg-[#1877F2] rounded-full flex items-center justify-center transition-colors">
                  <Facebook className="w-5 h-5" />
                </a>
                <a href="https://www.instagram.com/asofom_mx" target="_blank" rel="noopener noreferrer"
                   className="w-10 h-10 bg-white/10 hover:bg-gradient-to-tr hover:from-[#FD5949] hover:via-[#D6249F] hover:to-[#285AEB] rounded-full flex items-center justify-center transition-colors">
                  <Instagram className="w-5 h-5" />
                </a>
              </div>
            </div>
            
            <div>
              <h3 className="text-white font-bold text-lg mb-4">{t('footer.quickLinks')}</h3>
              <ul className="space-y-3">
                <li><a href="#gracias" className="hover:text-[#FF6B35] transition-colors">{language === 'es' ? 'Así se vivió' : 'How it was lived'}</a></li>
                <li><a href="/agenda" className="hover:text-[#FF6B35] transition-colors">{t('nav.agenda')}</a></li>
                <li><a href="#speakers" className="hover:text-[#FF6B35] transition-colors">{t('nav.speakers')}</a></li>
                <li><a href="/ranking" className="hover:text-[#FF6B35] transition-colors font-bold text-[#D4AF37]" data-testid="footer-ranking">Ranking ASOFOM 2026</a></li>
                <li><a href="/app-asofom" className="hover:text-[#E55A28] transition-colors font-bold text-[#FF6B35]" data-testid="footer-app">📱 App ASOFOM</a></li>
                <li><a href="/perspectivas" className="hover:text-[#FF6B35] transition-colors" data-testid="footer-perspectivas">Perspectivas</a></li>
                <li><a href="/patrocinios" className="hover:text-[#FF6B35] transition-colors" data-testid="footer-patrocinios">{language === 'es' ? 'Patrocinar 2027' : 'Sponsor 2027'}</a></li>
                <li><a href="#preregistro" className="hover:text-[#FF6B35] transition-colors font-bold text-[#FF6B35]" data-testid="footer-preregistro">{language === 'es' ? '🔥 Pre-registro 2027' : '🔥 Pre-register 2027'}</a></li>
                <li><a href="https://www.asofom.mx" target="_blank" rel="noopener noreferrer" className="hover:text-[#FF6B35] transition-colors">{t('footer.asofomSite')}</a></li>
              </ul>
            </div>
            
            <div>
              <h3 className="text-white font-bold text-lg mb-4">{t('footer.contact')}</h3>
              <ul className="space-y-3">
                <li className="flex items-center space-x-3">
                  <Phone className="w-5 h-5 text-[#FF6B35] flex-shrink-0" />
                  <a href="tel:5590004959" className="hover:text-[#FF6B35] transition-colors">55 9000 4959</a>
                </li>
                <li className="flex items-center space-x-3">
                  <Mail className="w-5 h-5 text-[#FF6B35] flex-shrink-0" />
                  <a href="mailto:mercadotecnia@asofom.mx" className="hover:text-[#FF6B35] transition-colors">mercadotecnia@asofom.mx</a>
                </li>
                <li className="flex items-start space-x-3">
                  <MapPin className="w-5 h-5 text-[#FF6B35] flex-shrink-0 mt-1" />
                  <span>Hotel AVA Resort<br />Cancún, México</span>
                </li>
              </ul>
            </div>
          </div>
          
          <FooterAppDownload />
          <div className="border-t border-gray-700 pt-8">
            <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
              <p className="text-sm text-gray-400">
                © 2026 ASOFOM. {t('footer.rights')}
              </p>
              <div className="flex space-x-6 text-sm">
                <a href="#" className="text-gray-400 hover:text-[#FF6B35] transition-colors">{t('footer.privacy')}</a>
                <a href="#" className="text-gray-400 hover:text-[#FF6B35] transition-colors">{t('footer.terms')}</a>
              </div>
            </div>
          </div>
        </div>
      </footer>

      {/* Thank-you popup with official aftermovie */}
      <Suspense fallback={null}>
        <ThankYouPopup />
      </Suspense>

      {/* Mobile sticky pre-registro bar */}
      <StickyPreregistroBar />

      {/* WhatsApp Floating Button with post-event message */}
      <div className="fixed bottom-24 md:bottom-8 right-5 md:right-8 z-50">
        {/* Proactive message bubble */}
        <div className="hidden md:block absolute right-full mr-3 top-1/2 -translate-y-1/2 animate-bounce-subtle">
          <div className="bg-white text-gray-800 px-4 py-3 rounded-lg shadow-xl text-sm font-medium relative w-[250px]" data-testid="whatsapp-bubble">
            {language === 'es' ? '¡Gracias por acompañarnos en Cancún! ¿Comentarios sobre la Convención o quieres info de 2027?' : 'Thank you for joining us in Cancún! Feedback on the Convention or want 2027 info?'}
            <span className="block text-xs font-bold text-[#FF6B35] mt-1">{language === 'es' ? 'Nos vemos en 2027 · Escríbenos' : 'See you in 2027 · Message us'}</span>
            <div className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-full">
              <div className="border-8 border-transparent border-l-white"></div>
            </div>
          </div>
        </div>
        
        <a
          href={WHATSAPP_2027_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center justify-center bg-[#25D366] hover:bg-[#20BA5A] text-white rounded-full w-16 h-16 shadow-2xl hover:scale-110 transition-all duration-300"
          aria-label="WhatsApp"
        >
          <svg viewBox="0 0 24 24" className="w-8 h-8 fill-current">
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
          </svg>
        </a>
      </div>
    </div>
  );
};

export default LandingPage;
